import React, { useState, useEffect, useCallback } from "react";
import { ReloadOutlined, SearchOutlined, FileSearchOutlined, FilterOutlined } from "@ant-design/icons";
import { Button, Card, Col, Input, Row, Tag } from "antd/es";
import { listRemoteModels } from "../../api";

import CardTitle from "../CardTitle";
import { ModelList } from "./ModelList";
import { ModelDetails } from "./ModelDetails";
import { ModelFilters } from "./ModelFilters";

import store from "../../store";

import '../../style/model-storage.scss';

function pathSorter(a, b) {
    const partsA = a.path.split('/');
    const partsB = b.path.split('/');

    const dateA = partsA[0];
    const dateB = partsB[0];

    if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
    } else {
        const timeA = partsA[1];
        const timeB = partsB[1];

        if (timeA !== timeB) {
            return timeA.localeCompare(timeB);
        } else {
            const checkpointA = Number(partsA[2].substr(11));
            const checkpointB = Number(partsB[2].substr(11));

            return checkpointA - checkpointB;
        }
    }
}

const ModelStorageBrowser = ({ currentModel, setCurrentModel, derive }) => {
    const [showFilters, setShowFilters] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [filters, setFilters] = useState({ query: '', localOnly: false, selecting: true });
    const [selectedModel, setSelectedModel] = useState({ open: false, model: null });
    const [loading, setLoading] = useState(false);

    const remoteModels = store.models.useRemoteModels();
    const setRemoteModels = store.models.useSetRemoteModels();
    const diskUsage = store.models.useDiskUsage();


    const updateModels = useCallback(() => {
        setLoading(true);
        listRemoteModels()
            .then(models => models.filter(m => m.path.split('/').length > 4).map(model => {
                const [date, time, checkpoint] = model.path.split('/');
                const path = `20${date}/${time}/${checkpoint}/unet`;

                return {
                    ...model,
                    path,
                    fullPath: model.path,
                    step: Number(checkpoint.split("checkpoint-")[1]) | 0
                };
            })).then(models => {
                models.sort((a, b) => pathSorter(b, a));
                return models;
            }).then(setRemoteModels).then(() => setLoading(false));
    }, [setRemoteModels, setLoading]);

    useEffect(() => {
        updateModels();
    }, [updateModels]);

    const totalSize = remoteModels.map(m => m.size).reduce((a, b) => a + b, 0);
    const totalSizeGb = (totalSize / 1024 / 1024 / 1024).toFixed(0);

    const bodyStyle = { padding: '0 !important' };

    if (collapsed) {
        bodyStyle.display = 'none';
    }

    const [size, diskUsed, diskFree] = diskUsage || [0, 0, 0];
    const free = (diskFree / 1024 / 1024 / 1024).toFixed(0);

    const title = <CardTitle icon={<FileSearchOutlined />} collapsed={collapsed} setCollapsed={setCollapsed}>
        Model Storage
        <Tag color="success" size={"small"} style={{ marginLeft: 4, padding: '0 4px', fontSize: 11 }}>{totalSizeGb} GB</Tag>
        <Tag color="info" size={"small"} style={{ marginLeft: 4, padding: '0 4px', fontSize: 11 }}>{free} GB free</Tag>
        <Button size="small" onClick={e => { updateModels(); e.preventDefault(); e.stopPropagation(); }} disabled={loading} loading={loading} icon={<ReloadOutlined style={{ fontSize: 12 }} />} />
    </CardTitle>;

    const clearSelection = store.models.useClearSelection();

    return (
        <Card title={title} className="run-container settings-card model-storage" style={{ height: '100%' }} bodyStyle={bodyStyle}>
            {!collapsed && <Row>
                <Col span={24}>
                    <Input className="model-search-field"
                        placeholder="Search..."
                        value={filters.query}
                        onChange={e => setFilters(f => ({ ...f, query: e.target.value }))}
                        prefix={<SearchOutlined style={{ padding: '0 0' }} />}
                        addonAfter={<Button style={{ height: 'auto', width: 'auto' }} onClick={() => setShowFilters(v => !v)} type={showFilters ? "primary" : "default"} icon={<FilterOutlined style={{ padding: '0 8px' }} />} />}
                    />
                </Col>

                {showFilters && <Col span={24}>
                    <ModelFilters {...{ filters, setFilters, clearSelection }} />
                </Col>}

                <Col span={24} style={{ overflow: 'hidden' }}>
                    <div className={"model-list-wrapper" + (selectedModel.open ? " slide" : "")}>
                        <ModelList {...{ remoteModels, filters, currentModel, setCurrentModel }} onClick={m => setSelectedModel(v => ({ ...v, model: m, open: true }))} />

                        <div className="off-canvas">
                            {selectedModel.model && <ModelDetails {...{ selectedModel: selectedModel.model, derive }} close={() => setSelectedModel(v => ({ ...v, open: false }))} />}
                        </div>
                    </div>
                </Col>
            </Row>}
        </Card>
    )
};
// PaginationConfig
export const paginationConfig = {
    position: 'bottom',
    simple: true,
    align: 'center',
    size: 'small',
    showQuickJumper: false,
    totalBoundaryShowSizeChanger: 50000,
    pageSize: 20
};

export function analyzeLosses(lines) {
    const results = [];
    let hasNans = false;

    for (const line of lines) {
        const [file, losses] = line.split(": ");

        const steps = losses.split("), (").map(s => s.replace("(", "").replace(")", "").trim()).map(parseStep).filter(s => s);
        for (const s of steps) {
            if (isNaN(s.loss)) {
                hasNans = true;
                s.loss = 0.0;
            }
        }

        results.push([file, steps]);
    }

    // Compute the averages each timestep
    const timestepLosses = Array.from({ length: 1000 }, () => []);
    const averages = Array.from({ length: 1000 }, () => ({ min: 0.0, max: 0.0, avg: 0.0 }));

    for (const [, losses] of results) {
        for (const { timestep, loss } of losses) {
            timestepLosses[timestep].push(loss);
        }
    }

    const timestepFreq = timestepLosses.map(l => l.length);

    // Compute averages per timestep
    for (let i = 0; i < 1000; i++) {
        const losses = timestepLosses[i];
        const sum = losses.reduce((acc, v) => acc + v, 0);
        const min = losses.reduce((acc, v) => Math.min(acc, v), 0xFFFFFF);
        const max = losses.reduce((acc, v) => Math.max(acc, v), 0);

        averages[i].avg = losses.length ? (sum / losses.length) : 0.0;
        averages[i].max = losses.length ? max : 0.0;
        averages[i].min = losses.length ? min : 0.0;
    }

    const relativeAverages = {};

    // Compute losses per file relative to the average loss
    for (const [file, steps] of results) {
        const relative = steps.map(({ timestep, loss }) => loss / averages[timestep].avg);
        const relAvg = relative.reduce((acc, v) => acc + v, 0) / relative.length;

        relativeAverages[file] = relAvg;
    }

    const result = {
        losses: Object.fromEntries(results),
        relative: relativeAverages,
        overall: averages.map(avg => avg.avg),
        averages,
        timestepFrequencies: timestepFreq,
        hasNans,
    };

    console.log(result);
    return result;
}

function parseStep(s) {
    const values = s.split(",").map(parseFloat);
    if (values.length < 3) {
        return null;
    }

    const [step, timestep, loss, caption, ...rest] = values;
    return { step, timestep, loss, caption };
}


export default ModelStorageBrowser;
